<script setup lang="ts">

import { type Ref, ref, computed } from "vue";
import { States } from "@domain/General";
import { Field, Validation } from "@library/Components/Forms/Form";
import { useUser } from "@domain/Security";
import { makeForm } from "@library/Components/Forms/Form";
import type { IActivateVehicleModel } from '../IActivateVehicleModel';
import type { IDealerModeVehicle } from "@app/DealerMode/Types/IDealerModeVehicle";
import { useDealerModeApi } from "@app/DealerMode/Types/Api/DealerModeApi";
import { onBeforeMount } from "vue";
import type { IDealerModeAppConfiguration } from "@domain/AppConfiguration/DealerModeAppConfiguration";
import { Loading } from "@app/Components/Layout";

const user = useUser();
const findVehicleModel = defineModel<IActivateVehicleModel>( { required: true } );
const vehicleForm = makeForm( findVehicleModel.value.vehicle! );

let availableApps : Ref<IDealerModeAppConfiguration[]> = ref([]);
let dataLoaded = ref( false );

// Sets the autofocus on the first unfilled field
enum FOCUS { App = 0, Rego = 1, RegoState = 2, Odometer = 3, Mobile = 4, Submit = 5 };
const focusIndex = computed( () =>
    ( findVehicleModel.value.vehicle?.appConfigurationId ?? '' ).length == 0 ? FOCUS.App :
    ( findVehicleModel.value.vehicle?.rego ?? '' ).length == 0 ? FOCUS.Rego :
    ( findVehicleModel.value.vehicle?.regoState ?? '' ).length == 0 ? FOCUS.RegoState :
    ( findVehicleModel.value.vehicle?.odometer ?? 0 ) < 1 ? FOCUS.Odometer :
    ( findVehicleModel.value.vehicle?.mobile ?? '' ).length == 0 ? FOCUS.Mobile :
    FOCUS.Submit
)

// Validation rules
const validation = {
    rego: Validation.required( "Rego is required", 2, 9 ),
    regoState: Validation.required( "Please select the state your vehicle is registered in", 2 ),
    odometer: [
        Field.required( "Please enter your odometer", 0, 999999999 ),
        Field.numbersOnly( "Please enter a valid odometer value" )
    ],
    app: Validation.required( "App is required" ),
    healthMonitor: Validation.required( "Health Monitor status is required" )
};

/*
* fetch the data this component is responsible for managing
*/
onBeforeMount( async () => {
    // once we implement role switching, this will feed off of the apps on the selected role
    // however for now we are just getting all the apps from across all the roles
    const appIds = user.roles.map( r => r.appIds ).flat();
    const dealerModeApi = useDealerModeApi();
    const getAppsResponse = await dealerModeApi.listAppConfigurationsAsync( appIds );

    availableApps.value = getAppsResponse.data.appConfigurations;
    dataLoaded.value = true;
});

</script>

<template>

    <div v-if="!dataLoaded" class="flex h-full w-full justify-center items-center align-center">
        <Loading />
    </div>

    <Form v-else
          :form="vehicleForm"
          v-slot="{ form, model }"
          class="flex flex-col gap-4 3xl:gap-6 3xl:max-w-md 3xl:mx-auto w-full pb-12 pt-4"
          @ok="async ( data: IDealerModeVehicle ) => findVehicleModel.activateVehicle( data )">

        <div class="text-sm lg:text-base 3xl:text-lg text-center tracking-wider">
            <div v-if="model.vehicleId == null">
                <p class="text-white">This car is new to Carbucks</p>
                <p class="opacity-60">Please complete the following fields</p>
            </div>
            <div v-else>
                <p class="text-white">Let's activate this vehicle</p>
                <p class="opacity-60">Please complete the remaining fields</p>
            </div>
        </div>

        <SelectList v-if="availableApps.length > 1"
                    :autofocus="focusIndex == FOCUS.App"
                    label="App"
                    placeholder="Select App"
                    v-model="model.appConfigurationId"
                    :rules="validation.app"
                    required
                    hide-details="auto"
                    item-title="name"
                    item-value="appConfigurationId"
                    :items="availableApps"
        />

        <TextField label="Vehicle Rego"
                   :autofocus="focusIndex == FOCUS.Rego"
                   v-model="model.rego"
                   hide-details="auto"
                   :rules="validation.rego"
                   required
                   min="3"
                   max="9"
                   @input="() => { model.query = ( model.query ?? '' ).toUpperCase() }"
        />

        <SelectList label="Vehicle Registration State"
                    :autofocus="focusIndex == FOCUS.RegoState"
                    placeholder="Select registration state"
                    v-model="model.regoState"
                    :rules="validation.regoState"
                    required
                    hide-details="auto"
                    :items="States.toListItems()"
        />

        <TextField label="Current Odometer"
                   :autofocus="focusIndex == FOCUS.Odometer"
                   v-model="model.odometer"
                   min="0"
                   hide-details="auto"
                   :rules="validation.odometer"
                   required
                   type="number"
                   suffix="km"
        />

        <MobilePhoneTextField v-model="model.mobile"
                              :autofocus="focusIndex == FOCUS.Mobile"
                              label="Customer's Mobile"
                              required
                              hide-details="auto"
        />

        <div class="my-1 h-1 bg-white/5 rounded mx-auto w-12" />

        <!-- SUBMIT BUTTON -->
        <button class="flex flex-col justify-center items-center rounded-md p-4 gap-1
                       cursor-pointer transition-all
                       bg-secondary-500 hover:brightness-110"
                :class="{ 'opacity-20 !bg-white/10': form.isInvalid || form.isClean }"
                :disabled="form.isInvalid || form.isClean"
                v-ripple
                type="submit">

            <h1 class="flex items-center uppercase 3xl:text-xl leading-[1.2] font-bold">
                Activate Vehicle
            </h1>

            <div class="text-sm 3xl:text-lg opacity-60 leading-[1.2]">
                {{ model.vehicleId == null ? 'Add the vehicle to the Car Parc' : 'Activate the vehicle' }}
                and send the Welcome SMS with a link to download the Carbucks app
            </div>

        </button>

    </Form>

</template>
